import { useCallback, useEffect, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Menu as MenuIcon } from "lucide-react";
import Swal, { getApparifyLogoutDialogOptions } from "../../utils/apparifySwal";

import { useAuth } from "../../context/AuthContext";
import AdminSidebar from "./AdminSidebar";
import AdminTopbar from "./AdminTopbar";
import { IconButton, Sheet } from "../../components/ui";
import "../../styles/operations-portal.css";
import "../../business/business-ui.css";

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const loggingOutRef = useRef(false);
  const contentRef = useRef(null);

  const scrollAdminContentToTop = useCallback(() => {
    if (contentRef.current) contentRef.current.scrollTop = 0;
    const pageScroller = document.scrollingElement || document.documentElement;
    if (pageScroller) pageScroller.scrollTop = 0;
    if (document.body) document.body.scrollTop = 0;
  }, []);

  const handleSidebarNavigate = useCallback(() => {
    scrollAdminContentToTop();
    setMobileNavOpen(false);
  }, [scrollAdminContentToTop]);

  const handleLogout = async () => {
    if (loggingOutRef.current) return;
    loggingOutRef.current = true;
    try {
      const result = await Swal.fire({
        ...getApparifyLogoutDialogOptions("admin"),
      });
      if (!result.isConfirmed) return;

      await logout();
      await Swal.fire({
        icon: "success",
        title: "Signed out successfully",
        html: "Redirecting you to the admin login page...",
        timer: 900,
        showConfirmButton: false,
      });
      navigate("/admin/login", { replace: true });
    } catch (error) {
      await Swal.fire({
        icon: "error",
        title: "Logout failed",
        text: error?.message || "Unable to logout. Please try again.",
        confirmButtonText: "OK",
      });
    } finally {
      loggingOutRef.current = false;
    }
  };

  useEffect(() => setMobileNavOpen(false), [pathname]);

  useEffect(() => {
    scrollAdminContentToTop();
  }, [pathname, scrollAdminContentToTop]);

  useEffect(() => {
    document.body.classList.add("admin-portal-active");
    return () => document.body.classList.remove("admin-portal-active");
  }, []);

  useEffect(() => {
    const id = "admin-swal-root";
    let node = document.getElementById(id);
    if (!node) {
      node = document.createElement("div");
      node.id = id;
      document.body.appendChild(node);
    }
    return () => {};
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mediaQuery = window.matchMedia("(min-width: 768px)");
    const handleChange = (event) => {
      if (event.matches) setMobileNavOpen(false);
    };
    if (mediaQuery.matches) setMobileNavOpen(false);
    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }
    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  return (
    <div className="admin-portal min-h-screen bg-canvas text-foreground md:h-screen md:overflow-hidden">
      <div className="admin-app-frame mx-auto min-h-screen w-full md:h-full">
        <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen} title="Admin navigation" titleHidden>
          <AdminSidebar onNavigate={handleSidebarNavigate} onClose={() => setMobileNavOpen(false)} />
        </Sheet>

        <div className="flex min-h-screen w-full md:h-full md:min-h-0">
          <aside className="admin-sidebar-shell hidden h-full min-h-0 w-[var(--layout-sidebar)] shrink-0 overflow-hidden border-r border-default bg-surface md:block">
            <AdminSidebar onNavigate={handleSidebarNavigate} />
          </aside>
          <div className="flex min-w-0 flex-1 flex-col md:h-full md:min-h-0">
            <header className="admin-topbar-shell sticky top-0 z-30 h-[var(--layout-topbar)] shrink-0 border-b border-default bg-surface">
              <div className="flex h-full items-center gap-2 px-2 md:px-6">
                <IconButton icon={MenuIcon} label="Open menu" onClick={() => setMobileNavOpen(true)} className="md:hidden" />
                <div className="h-full min-w-0 flex-1">
                  <AdminTopbar user={user} onLogout={handleLogout} />
                </div>
              </div>
            </header>
            <main ref={contentRef} className="admin-content min-h-0 min-w-0 flex flex-1 flex-col overflow-y-auto overflow-x-hidden px-4 py-5 scrollbar-fade md:px-6 md:py-6">
              <Outlet />
            </main>
          </div>
        </div>
      </div>
      <div id="admin-swal-root" />
    </div>
  );
}
